/**
 * Tenants Browser
 *
 * Super admin page: list all tenants from the database.
 * No mock data — DB is the source of truth (build contract).
 */

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Search,
  Plus,
  RefreshCcw,
  ChevronDown,
  FolderOpen,
} from 'lucide-react';
import { listAllTenants, type TenantOverview, type TenantPlan, type TenantStatus } from '../../lib/superAdminService';

type FilterKey = 'status' | 'plan';
interface Filters {
  status?: TenantStatus;
  plan?: TenantPlan;
}

export default function TenantsBrowser() {
  const [tenants, setTenants] = useState<TenantOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<Filters>({});

  const loadData = async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await listAllTenants();
      setTenants(data);
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredTenants = useMemo(() => {
    return tenants.filter((t) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (
          !t.name.toLowerCase().includes(q) &&
          !t.slug.toLowerCase().includes(q)
        ) {
          return false;
        }
      }
      if (filters.status && t.status !== filters.status) return false;
      if (filters.plan && t.plan !== filters.plan) return false;
      return true;
    });
  }, [tenants, searchQuery, filters]);

  const setFilter = (key: FilterKey, value: string | undefined) => {
    if (value === undefined) {
      setFilters((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } else {
      setFilters((prev) => ({ ...prev, [key]: value as TenantStatus | TenantPlan }));
    }
  };

  const clearFilters = () => setFilters({});
  const hasActiveFilters = Object.keys(filters).length > 0;

  const planColors: Record<string, string> = {
    free: 'bg-[var(--hover-tint)] text-[var(--text-sec)]',
    starter: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
    pro: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
    professional: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
    enterprise: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  };
  const statusColors: Record<string, string> = {
    active: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
    suspended: 'bg-[var(--coral-dim)] text-[var(--coral)]',
    pending: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-[var(--hover-tint)] rounded w-48" />
          <div className="h-64 bg-[var(--hover-tint)]" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-pri)]">Tenants</h1>
          <p className="text-[var(--text-ter)] mt-1">
            Organizations on the platform. Each tenant has one or more workspaces.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 border border-[var(--rule)] hover:bg-[var(--surface)]"
          >
            <RefreshCcw className="h-4 w-4" />
            Refresh
          </button>
          <Link
            to="/platform-admin/tenants/new"
            className="flex items-center gap-2 px-4 py-2 bg-[var(--coral)] text-white hover:bg-[var(--coral)]"
          >
            <Plus className="h-4 w-4" />
            New Tenant
          </Link>
        </div>
      </div>

      {error && (
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-4 flex items-center justify-between">
          <span className="text-[var(--coral)]">{error}</span>
          <button
            type="button"
            onClick={loadData}
            className="shrink-0 px-3 py-1.5 bg-[var(--coral-dim)] text-[var(--coral)] hover:bg-[var(--coral-dim)]"
          >
            Retry
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[var(--text-ter)]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search tenants..."
            className="w-full pl-10 pr-4 py-2.5 border border-[var(--rule)] focus:ring-2 focus:ring-red-500"
          />
        </div>
        <div className="relative">
          <select
            value={filters.status || ''}
            onChange={(e) => setFilter('status', e.target.value || undefined)}
            className="appearance-none pl-4 pr-10 py-2.5 border border-[var(--rule)] bg-[var(--bg-raised)] focus:ring-2 focus:ring-red-500"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="pending">Pending</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)] pointer-events-none" />
        </div>
        <div className="relative">
          <select
            value={filters.plan || ''}
            onChange={(e) => setFilter('plan', e.target.value || undefined)}
            className="appearance-none pl-4 pr-10 py-2.5 border border-[var(--rule)] bg-[var(--bg-raised)] focus:ring-2 focus:ring-red-500"
          >
            <option value="">All plans</option>
            <option value="free">Free</option>
            <option value="starter">Starter</option>
            <option value="pro">Pro</option>
            <option value="enterprise">Enterprise</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)] pointer-events-none" />
        </div>
        {hasActiveFilters && (
          <button onClick={clearFilters} className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            Clear filters
          </button>
        )}
      </div>

      <p className="text-sm text-[var(--text-ter)]">
        Showing {filteredTenants.length} of {tenants.length} tenants
      </p>

      {filteredTenants.length === 0 ? (
        <div className="text-center py-12 bg-[var(--bg-raised)] border border-[var(--rule)]">
          <Building2 className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[var(--text-pri)] mb-1">No tenants found</h3>
          <p className="text-[var(--text-ter)] mb-4">
            {tenants.length === 0
              ? 'Create a tenant to get started. Each tenant gets a default workspace.'
              : 'Try adjusting your search or filters.'}
          </p>
          {tenants.length === 0 && (
            <Link
              to="/platform-admin/tenants/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--coral)] text-white hover:bg-[var(--coral)]"
            >
              <Plus className="h-4 w-4" />
              New Tenant
            </Link>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTenants.map((t) => (
            <div
              key={t.id}
              className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-[var(--rule)] hover:shadow-md transition-all"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[var(--hover-tint)]">
                  <Building2 className="h-6 w-6 text-[var(--text-sec)]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-[var(--text-pri)] truncate">{t.name}</h3>
                  <p className="text-sm text-[var(--text-ter)] truncate">{t.slug}</p>
                  {t.description && (
                    <p className="text-sm text-[var(--text-sec)] mt-1 line-clamp-2">{t.description}</p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[t.status] || 'bg-[var(--hover-tint)] text-[var(--text-sec)]'}`}>
                      {t.status}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${planColors[t.plan] || 'bg-[var(--hover-tint)] text-[var(--text-sec)]'}`}>
                      {t.plan}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 mt-2 text-sm text-[var(--text-ter)]">
                    <FolderOpen className="h-4 w-4" />
                    {t.workspaceCount} workspace{t.workspaceCount !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
